import { api } from './api';
import { TimeRange, MarketStatus, SearchResult as MarketSearchResult, StockPrice } from '../types/market';
import { RawCompanyData } from '../types/market';

// Utility for retry logic
const retry = async <T>(
  fn: () => Promise<T>,
  retries = 3,
  delay = 1000,
  backoff = 2
): Promise<T> => {
  try {
    return await fn();
  } catch (error) {
    if (retries <= 0) throw error;
    await new Promise(resolve => setTimeout(resolve, delay));
    return retry(fn, retries - 1, delay * backoff, backoff);
  }
};

// Remove unused imports

export interface CompanyDetails {
  ticker: string;
  name: string;
  description: string;
  sector: string;
  industry: string;
  marketCap: number;
  employees: number;
  homepage: string;
  exchange: string;
  pe_ratio?: number;
  beta?: number;
}

export type SearchResult = MarketSearchResult;

// Centralized interface for consistent price data
export interface NormalizedPriceData {
  currentPrice: number;
  previousClose: number;
  change: number;
  changePercent: number;
  high: number;
  low: number;
  open: number;
  volume: number;
  timestamp: number;
  marketStatus: string;
  currency: string;
}

// Keep only the interfaces we're using
interface WatchedStock {
  id: string;
  symbol: string;
  order: number;
}

interface WatchedStockWithData extends WatchedStock {
  price: number;
  change: number;
  changePercent: number;
}

interface MarketIndex {
  symbol: string;
  name: string;
  change: number;
  level: number;
}

interface MarketDataService {
  saveWatchedStock(stock: WatchedStock): Promise<void>;
  removeWatchedStock(stockId: string): Promise<void>;
  getWatchedStocks(): Promise<WatchedStockWithData[]>;
  getStockPrice(symbol: string): Promise<{
    price: number;
    change: number;
    changePercent: number;
  }>;
}

class MarketDataService {
  private static instance: MarketDataService;
  private cache: Map<string, { data: any; timestamp: number }> = new Map();
  private readonly CACHE_DURATION = 15 * 60 * 1000; // 15 minutes
  private readonly RATE_LIMIT_WINDOW = 60 * 1000; // 1 minute
  private requestCounts: Map<string, number> = new Map();
  // Add a cache of current price data to ensure consistency
  private currentPricesCache: Map<string, { data: NormalizedPriceData; timestamp: number }> = new Map();
  // Shorter cache duration for current price data
  private readonly CURRENT_PRICE_CACHE_DURATION = 60 * 1000; // 1 minute

  private constructor() {}

  static getInstance(): MarketDataService {
    if (!MarketDataService.instance) {
      MarketDataService.instance = new MarketDataService();
    }
    return MarketDataService.instance;
  }

  private generateCacheKey(endpoint: string, params: any): string {
    return `${endpoint}:${JSON.stringify(params)}`;
  }

  private async fetchWithCache<T>(
    endpoint: string,
    params: any,
    fetcher: () => Promise<T>
  ): Promise<T> {
    const cacheKey = this.generateCacheKey(endpoint, params);
    const cached = this.cache.get(cacheKey);

    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.data as T;
    }

    try {
      const data = await fetcher();
      this.cache.set(cacheKey, { data, timestamp: Date.now() });
      return data;
    } catch (error) {
      console.log(`Market data fetch failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // Add this method to your MarketDataService class
  async getStockNews(symbol: string, limit: number = 5): Promise<any[]> {
    try {
      console.log('Fetching news for:', symbol);
      
      const { data } = await api.get<any>(`/market/stock/${symbol}/news`, {
        params: { limit }
      });
  
      // Validate the response
      if (!data || !Array.isArray(data)) {
        console.log('Invalid news data structure:', data);
        return [];
      }
  
      return data;
    } catch (error) {
      console.error(`Failed to fetch news for ${symbol}:`, error);
      return [];
    }
  }

  async getMarketIndices(): Promise<MarketIndex[]> {
    try {
      const { data } = await api.get('/market/indices');
      return data;
    } catch (error) {
      console.error('Failed to fetch market indices:', error);
      throw error;
    }
  }

  async searchStocks(query: string): Promise<SearchResult[]> {
    try {
      const response = await api.get<SearchResult[]>('/market/search', {
        params: { q: query }
      });
      const { data } = response;
      
      // Check if we received HTML instead of JSON (API redirect issue)
      if (typeof data === 'string' && (data as string).includes('<!doctype html>')) {
        console.warn(`Received HTML response for stock search. API may be misconfigured or down.`);
        throw new Error('API returned HTML instead of JSON data');
      }
      
      return data;
    } catch (error) {
      console.log('Stock search failed:', error);
      throw error;
    }
  }

  // Add these methods to the MarketDataService class
  
  async getEnhancedStockSearch(query: string): Promise<SearchResult[]> {
    try {
      const results = await this.searchStocks(query);
      
      // Enhance results with additional data in parallel
      const enhancedResults = await Promise.all(
        results.map(async (result) => {
          try {
            // Use getCurrentPrice as the source of truth
            const [priceData, details] = await Promise.all([
              this.getCurrentPrice(result.ticker),
              this.getStockDetails(result.ticker),
              this.getStockNews(result.ticker, 1)
            ]);
  
            return {
              ...result,
              price: priceData.currentPrice,
              change: priceData.change,
              changePercent: priceData.changePercent,
              volume: priceData.volume,
              sector: details.sector,
              marketCap: details.marketCap,
              pe_ratio: details.pe_ratio,
              beta: details.beta
            };
          } catch (error) {
            console.warn(`Failed to enhance search result for ${result.ticker}:`, error);
            return result;
          }
        })
      );
  
      return enhancedResults;
    } catch (error) {
      console.error('Enhanced stock search failed:', error);
      throw error;
    }
  }

  async getPopularStocks() {
    try {
      const response = await api.get('/market/popular');
      return response.data;
    } catch (error) {
      console.log('Failed to fetch popular stocks:', error);
      throw new Error('Failed to fetch popular stocks');
    }
  }

  async getMarketStatus(): Promise<MarketStatus> {
    try {
      const { data } = await api.get<MarketStatus>('/market/status');
      return data;
    } catch (error) {
      console.log('Market status fetch failed:', error);
      throw error;
    }
  }

  private getTimeRangeDays(range: TimeRange): number {
    const ranges: Record<TimeRange, number> = {
      '1d': 1,
      '5d': 5,
      '1mo': 30,
      '3mo': 90,
      '6mo': 180,
      '1y': 365,
      '2y': 730,
      '5y': 1825,
      'max': 9999
    };
    return ranges[range] || 30;
  }

  async getStockChartData(symbol: string, range: TimeRange = '1d'): Promise<StockPrice[]> {
    try {
      const response = await api.get(`/market/stock/${symbol}`, {
        params: { range }
      });

      return response.data.map((item: any) => ({
        ticker: symbol,
        t: new Date(item.t).getTime(),
        o: Number(item.o),
        h: Number(item.h),
        l: Number(item.l),
        c: Number(item.c),
        v: Number(item.v)
      }));
    } catch (error) {
      console.log('Failed to fetch stock data:', error);
      throw new Error('Failed to fetch stock data');
    }
  }

  // client/src/services/marketDataService.ts

  // marketDataService.ts

  // Add these methods to your MarketDataService class

  async getPublicCompanyDetails(symbol: string): Promise<CompanyDetails> {
    try {
      const cacheKey = `company-details:${symbol}`;
      const cached = this.getFromCache(cacheKey);
      
      if (cached) {
        return cached;
      }
      
      const response = await fetch(`/api/public/market/companies/${symbol}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch company details: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Store in cache for 10 minutes
      this.setCache(cacheKey, data);
      
      return data;
    } catch (error) {
      console.error('Error fetching public company details:', error);
      
      // Return minimal information if the API fails
      return {
        ticker: symbol,
        name: symbol,
        description: 'Company description not available',
        sector: '',
        industry: '',
        marketCap: 0,
        employees: 0,
        homepage: '',
        exchange: ''
      };
    }
  }

  async getStockData(symbol: string, timeRange: TimeRange = '1d'): Promise<StockPrice[]> {
    try {
      console.log('Fetching stock data with improved handler:', { symbol, timeRange });
      
      // Get the current price first, to ensure consistency
      const currentPriceData = await this.getCurrentPrice(symbol);
      
      // Use the retry utility for better resilience
      const response = await retry(() => api.get<any>(`/market/stock/${symbol}`, {
        params: { timeframe: timeRange }
      }));
      const { data } = response;
      
      // Check if we received HTML instead of JSON (API redirect issue)
      if (typeof data === 'string' && (data as string).includes('<!doctype html>')) {
        console.warn(`Received HTML response for ${symbol} stock data. API may be misconfigured or down.`);
        throw new Error('API returned HTML instead of JSON data');
      }

      // Validate response structure
      if (!data || !Array.isArray(data.results)) {
        console.log('Invalid data structure:', data);
        throw new Error('Invalid stock data response');
      }

      // Store market status information from response if available
      const marketStatus = data.marketStatus || currentPriceData.marketStatus || 'unknown';
      const sessionType = data.sessionType || 'unknown';

      // Transform the data into StockPrice format with robust null/undefined handling
      const stockPrices = data.results.map((bar: any) => ({
        t: bar.t,  // timestamp
        o: Number(parseFloat(bar.o ?? 0).toFixed(2)),  // open - consistent decimal places
        h: Number(parseFloat(bar.h ?? 0).toFixed(2)),  // high - consistent decimal places
        l: Number(parseFloat(bar.l ?? 0).toFixed(2)),  // low - consistent decimal places
        c: Number(parseFloat(bar.c ?? 0).toFixed(2)),  // close - consistent decimal places
        v: Number(bar.v ?? 0),  // volume
        // Include previous close and session info if available
        prevClose: data.previousClose ? Number(parseFloat(data.previousClose).toFixed(2)) : currentPriceData.previousClose,
        sessionType: bar.sessionType || sessionType,
        marketStatus: marketStatus
      })).filter((point: StockPrice) => 
        // Filter out any data points with invalid values
        point.t && !isNaN(point.o) && !isNaN(point.h) && 
        !isNaN(point.l) && !isNaN(point.c) && point.c > 0
      );

      // Ensure the data is sorted by timestamp
      const sortedPrices = stockPrices.sort((a: StockPrice, b: StockPrice) => a.t - b.t);
      
      // Important: For the latest data point, use the current price from our source of truth
      if (sortedPrices.length > 0) {
        const lastIndex = sortedPrices.length - 1;
        const now = Date.now();
        const lastPointTime = sortedPrices[lastIndex].t;
        
        // Only override the last point if it's from today
        if (now - lastPointTime < 24 * 60 * 60 * 1000) {
          sortedPrices[lastIndex].c = currentPriceData.currentPrice;
        }
      }
      
      // Get the last valid price (filtering out any potential bad data points)
      const lastPoint = sortedPrices.length > 0 ? sortedPrices[sortedPrices.length - 1] : null;
      
      // If we have a last valid price, ensure all data shows the same closing price
      if (lastPoint) {
        // Store the closing price as separate property for consistent display throughout UI
        // This comes from our source of truth for current prices
        const lastClose = currentPriceData.currentPrice;
        
        // Enhance the data with consistent closing prices and other metadata
        return sortedPrices.map((point: StockPrice) => ({
          ...point,
          latestClose: lastClose,
          // Calculate percentage change from previous close if available
          dayChangePercent: point.prevClose ? ((lastClose - point.prevClose) / point.prevClose) * 100 : null
        }));
      }
      
      return sortedPrices;
    } catch (error) {
      console.log('Stock data fetch failed:', {
        error,
        symbol,
        timeRange
      });
      
      // Check if we have cached data
      const cacheKey = this.generateCacheKey(`/market/stock/${symbol}`, { timeframe: timeRange });
      const cached = this.cache.get(cacheKey);
      if (cached) {
        console.log(`Using cached data for ${symbol} as fallback`);
        return cached.data;
      }
      
      throw error;
    }
  }

  async getPublicStockData(symbol: string, timeRange: TimeRange = '1d'): Promise<StockPrice[]> {
    try {
      console.log('Fetching public stock data with improved handler:', { symbol, timeRange });
      
      // Try to get current price data for consistency
      let currentPriceData: NormalizedPriceData | null = null;
      try {
        currentPriceData = await this.getCurrentPrice(symbol);
      } catch (e) {
        // If this fails, we'll continue without it
        console.warn('Failed to get current price for public data:', e);
        currentPriceData = null;
      }
      
      // Use 'range' as the parameter name to match server expectation
      // Apply retry logic to handle network failures
      const response = await retry(() => api.get<any>(`/public/market/stocks/${symbol}`, {
        params: { range: timeRange }
      }));
      const { data } = response;
      
      // Check if we received HTML instead of JSON (API redirect issue)
      if (typeof data === 'string' && (data as string).includes('<!doctype html>')) {
        console.warn(`Received HTML response for ${symbol} public stock data. API may be misconfigured or down.`);
        throw new Error('API returned HTML instead of JSON data');
      }
  
      // Add validation to check the response structure
      if (!data || !Array.isArray(data.results)) {
        console.log('Invalid data structure:', data);
        throw new Error('Invalid stock data response');
      }
  
      // Store market status information from response if available
      const marketStatus = data.marketStatus || (currentPriceData?.marketStatus || 'unknown');
      const sessionType = data.sessionType || 'unknown';
  
      // Transform the data into StockPrice format with robust null/undefined handling
      const stockPrices = data.results.map((bar: any) => ({
        t: bar.t,  // timestamp
        o: Number(parseFloat(bar.o ?? 0).toFixed(2)),  // open - consistent decimal places
        h: Number(parseFloat(bar.h ?? 0).toFixed(2)),  // high - consistent decimal places
        l: Number(parseFloat(bar.l ?? 0).toFixed(2)),  // low - consistent decimal places
        c: Number(parseFloat(bar.c ?? 0).toFixed(2)),  // close - consistent decimal places
        v: Number(bar.v ?? 0),  // volume
        // Include previous close and session info if available
        prevClose: data.previousClose ? Number(parseFloat(data.previousClose).toFixed(2)) : 
                  (currentPriceData ? currentPriceData.previousClose : undefined),
        sessionType: bar.sessionType || sessionType,
        marketStatus: marketStatus
      })).filter((point: StockPrice) => 
        // Filter out any data points with invalid values
        point.t && !isNaN(point.o) && !isNaN(point.h) && 
        !isNaN(point.l) && !isNaN(point.c) && point.c > 0
      );
  
      // Ensure the data is sorted by timestamp
      const sortedPrices = stockPrices.sort((a: StockPrice, b: StockPrice) => a.t - b.t);
      
      // If we have current price data, update the last point for consistency
      if (currentPriceData && sortedPrices.length > 0) {
        const lastIndex = sortedPrices.length - 1;
        const now = Date.now();
        const lastPointTime = sortedPrices[lastIndex].t;
        
        // Only override if it's from today
        if (now - lastPointTime < 24 * 60 * 60 * 1000) {
          sortedPrices[lastIndex].c = currentPriceData.currentPrice;
        }
      }
      
      // Add additional metadata to each data point
      // This makes it easier to use the data in charts and displays
      const enhancedPrices = sortedPrices.map((point: StockPrice) => {
        // For consistency, add the day's previous close to each point
        const prevClose = data.previousClose || 
                        (currentPriceData ? currentPriceData.previousClose : undefined);
        
        // Add the current price to the last point if available
        const isLastPoint = point === sortedPrices[sortedPrices.length - 1];
        const currentPrice = isLastPoint && currentPriceData ? currentPriceData.currentPrice : point.c;
        
        return {
          ...point,
          c: isLastPoint && currentPriceData ? currentPriceData.currentPrice : point.c,
          prevClose,
          // Calculate day change from previous close
          dayChange: prevClose ? Number((currentPrice - prevClose).toFixed(2)) : undefined,
          dayChangePercent: prevClose ? Number(((currentPrice - prevClose) / prevClose * 100).toFixed(2)) : undefined,
          // Always include current price for reference
          currentPrice: currentPriceData ? currentPriceData.currentPrice : undefined,
        };
      });
      
      return enhancedPrices;
    } catch (error) {
      console.log('Public stock data fetch failed:', error);
      
      // Check if we have cached data we can use as fallback
      const cacheKey = this.generateCacheKey(`/public/market/stocks/${symbol}`, { range: timeRange });
      const cached = this.cache.get(cacheKey);
      if (cached) {
        console.log(`Using cached data for ${symbol} as fallback`);
        return cached.data;
      }
      
      throw new Error('Failed to fetch stock data');
    }
  }

  async getTechnicalIndicator(symbol: string, indicator: string, params: any = {}) {
    try {
      console.log('Fetching technical indicator:', {
        symbol,
        indicator,
        params
      });

      const { data } = await api.get(
        `/market/stock/${symbol}/indicators/${indicator}`,
        { params }
      );

      if (!data) {
        throw new Error('Invalid technical indicator response');
      }

      return data;
    } catch (error) {
      console.log('Technical indicator fetch failed:', {
        error,
        symbol,
        indicator
      });
      throw error;
    }
  }

  async getVolumeProfile(symbol: string, timeRange: TimeRange) {
    const cacheKey = `volume:${symbol}:${timeRange}`;
    const cached = this.getFromCache(cacheKey);
    if (cached) return cached;

    try {
      const { data } = await api.get(`/market/stock/${symbol}/volume-profile`, {
        params: { range: timeRange }
      });
      this.setCache(cacheKey, data);
      return data;
    } catch (error) {
      console.log('Volume profile fetch failed:', error);
      throw error;
    }
  }

  async getStockDetails(symbol: string): Promise<CompanyDetails> {
    const cacheKey = `details:${symbol}`;
    const cached = this.getFromCache(cacheKey);
    if (cached) return cached;

    try {
      const response = await api.get<RawCompanyData>(`/market/stock/${symbol}/details`);
      const { data } = response;
      
      // Check if we received HTML instead of JSON (API redirect issue)
      if (typeof data === 'string' && (data as string).includes('<!doctype html>')) {
        console.warn(`Received HTML response for ${symbol} details. API may be misconfigured or down.`);
        throw new Error('API returned HTML instead of JSON data');
      }
      
      const processedData: CompanyDetails = {
        ticker: data.ticker,
        name: data.name,
        description: data.description,
        homepage: data.homepage || data.website || '',
        exchange: data.exchange || data.primaryExchange || '',
        marketCap: data.marketCap,
        employees: data.employees,
        sector: data.sector,
        industry: data.industry
      };

      this.setCache(cacheKey, processedData);
      return processedData;
    } catch (error) {
      console.log('Stock details fetch failed:', error);
      
      // Return minimal information if the API fails
      return {
        ticker: symbol,
        name: symbol,
        description: 'Company information unavailable',
        sector: '',
        industry: '',
        marketCap: 0,
        employees: 0,
        homepage: '',
        exchange: ''
      };
    }
  }

  async getSectorPerformance(symbol: string): Promise<any> {
    const cacheKey = `sector:${symbol}`;
    const cached = this.getFromCache(cacheKey);
    if (cached) return cached;

    try {
      const { data } = await api.get(`/market/sector-performance`, {
        params: { symbol }
      });
      this.setCache(cacheKey, data);
      return data;
    } catch (error) {
      console.log('Sector performance fetch failed:', error);
      throw error;
    }
  }

  // client/src/services/marketDataService.ts

  async getTechnicalIndicators(
    symbol: string,
    indicator: string,
    params: {
      period?: number;
      timeRange?: string;
    } = {}
  ): Promise<number[]> {
    try {
      const { data } = await api.get(`/market/stock/${symbol}/indicators/${indicator}`, {
        params: {
          period: params.period || 14
        }
      });
  
      // Convert the array of {timestamp, value} objects to just an array of values,
      // ordered by timestamp descending to match stock data
      if (Array.isArray(data)) {
        return data
          .sort((a, b) => a.timestamp - b.timestamp)
          .map(item => item.value);
      }
  
      throw new Error('Invalid indicator data format');
    } catch (error) {
      console.log('Technical indicator fetch failed:', error);
      throw new Error('Failed to fetch technical indicators');
    }
  }

  private getFromCache(key: string) {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.data;
    }
    return null;
  }

  private setCache(key: string, data: any) {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  /**
   * Gets consistent current price data for a stock regardless of the timeframe being viewed
   * This serves as the source of truth for current price across the application
   */
  async getCurrentPrice(symbol: string): Promise<NormalizedPriceData> {
    try {
      // Check cache first
      const cacheKey = `current-price:${symbol}`;
      const cached = this.currentPricesCache.get(cacheKey);
      const now = Date.now();
      
      // Use a shorter cache duration during market hours
      const isMarketHours = this.isWithinMarketHours();
      const cacheDuration = isMarketHours 
        ? 30 * 1000  // 30 seconds during market hours
        : this.CURRENT_PRICE_CACHE_DURATION;
      
      // Return cached data if it's fresh
      if (cached && now - cached.timestamp < cacheDuration) {
        return cached.data;
      }
      
      // Try multiple endpoints to ensure we get the most accurate data
      let data: any = null;
      let error: any = null;
      
      // Try primary quote endpoint - updated to use the correct path
      try {
        const response = await retry(() => api.get<any>(`/public/market/quote/${symbol}`));
        data = response.data;
        
        // Check if we received HTML instead of JSON (API redirect issue)
        if (typeof data === 'string' && data.includes('<!doctype html>')) {
          throw new Error('API returned HTML instead of JSON data');
        }
      } catch (err) {
        error = err;
        // Continue to next attempt
      }
      
      // If primary endpoint failed, try public endpoint
      if (!data) {
        try {
          const response = await retry(() => api.get<any>(`/public/market/stocks/${symbol}`));
          if (response.data && response.data.results && response.data.results.length > 0) {
            const latestBar = response.data.results[response.data.results.length - 1];
            data = {
              latestPrice: latestBar.c,
              previousClose: response.data.previousClose,
              high: latestBar.h,
              low: latestBar.l,
              open: latestBar.o,
              volume: latestBar.v
            };
          }
        } catch (err) {
          // If this also fails, we'll use the error from the first attempt
          if (!error) error = err;
        }
      }
      
      // If still no data, try snapshot endpoint - updated to use the correct path
      if (!data) {
        try {
          const response = await retry(() => api.get<any>(`/public/market/quote/${symbol}`));
          data = response.data;
        } catch (err) {
          // All attempts failed
          if (!error) error = err;
        }
      }
      
      // Check if we have any usable data at all
      if (!data) {
        console.warn(`No data received for ${symbol} quote`);
        throw error || new Error('Empty quote data response');
      }
      
      // Normalize the price data with more graceful fallbacks
      const normalizedData: NormalizedPriceData = {
        currentPrice: Number(parseFloat(data.latestPrice || data.c || data.close || data.price || 0).toFixed(2)),
        previousClose: Number(parseFloat(data.previousClose || data.pc || data.prevClose || 0).toFixed(2)),
        change: 0, // Will calculate below if possible
        changePercent: 0, // Will calculate below if possible
        high: Number(parseFloat(data.high || data.h || data.dayHigh || 0).toFixed(2)),
        low: Number(parseFloat(data.low || data.l || data.dayLow || 0).toFixed(2)),
        open: Number(parseFloat(data.open || data.o || data.openPrice || 0).toFixed(2)),
        volume: Number(data.volume || data.v || 0),
        timestamp: data.timestamp || data.t || now,
        marketStatus: data.marketStatus || this.determineMarketStatus(),
        currency: data.currency || 'USD'
      };
      
      // Calculate change and changePercent after we have the prices
      if (normalizedData.currentPrice > 0 && normalizedData.previousClose > 0) {
        normalizedData.change = Number((normalizedData.currentPrice - normalizedData.previousClose).toFixed(2));
        normalizedData.changePercent = Number(((normalizedData.change / normalizedData.previousClose) * 100).toFixed(2));
      } else if (data.change && !isNaN(Number(data.change))) {
        // If we have change directly from API
        normalizedData.change = Number(parseFloat(data.change).toFixed(2));
        normalizedData.changePercent = Number(parseFloat(data.changePercent || (normalizedData.change / normalizedData.previousClose * 100) || 0).toFixed(2));
      }
      
      // More lenient validation that accepts either current price or previous close
      const hasCurrentPrice = !isNaN(normalizedData.currentPrice) && normalizedData.currentPrice > 0;
      const hasPreviousClose = !isNaN(normalizedData.previousClose) && normalizedData.previousClose > 0;
      
      // We'll accept the data if either current price or previous close is valid
      const isValid = hasCurrentPrice || hasPreviousClose;
      
      // If current price is missing but we have previous close, use previous close as current price
      if (!hasCurrentPrice && hasPreviousClose) {
        normalizedData.currentPrice = normalizedData.previousClose;
        console.warn(`Using previousClose as currentPrice for ${symbol} as fallback`);
      }
      
      // If previous close is missing but we have current price, use current price as previous close
      if (!hasPreviousClose && hasCurrentPrice) {
        normalizedData.previousClose = normalizedData.currentPrice;
        console.warn(`Using currentPrice as previousClose for ${symbol} as fallback`);
      }
      
      if (!isValid) {
        console.warn(`Invalid price data received for ${symbol}:`, data);
        throw new Error('Invalid price data received from API');
      }
      
      // Final safety check - ensure we have at least a current price
      if (normalizedData.currentPrice <= 0) {
        console.warn(`Zero or negative price for ${symbol}:`, normalizedData);
        throw new Error('Invalid price value');
      }
      
      // Cache the normalized data
      this.currentPricesCache.set(cacheKey, { 
        data: normalizedData, 
        timestamp: now 
      });
      
      return normalizedData;
    } catch (error) {
      console.error(`Failed to fetch current price data for ${symbol}:`, error);
      
      // Check if we have a stale cache entry we can use
      const staleCache = this.currentPricesCache.get(`current-price:${symbol}`);
      if (staleCache) {
        console.log(`Using stale cache for ${symbol} as fallback`);
        return staleCache.data;
      }
      
      // If all else fails, return a default object with minimal data
      // This allows the UI to continue functioning
      return {
        currentPrice: 0,
        previousClose: 0,
        change: 0,
        changePercent: 0,
        high: 0,
        low: 0,
        open: 0,
        volume: 0,
        timestamp: Date.now(),
        marketStatus: 'error',
        currency: 'USD'
      };
    }
  }

  private isWithinMarketHours(): boolean {
    const now = new Date();
    
    // Get Eastern Time
    const etOptions: Intl.DateTimeFormatOptions = {
      timeZone: 'America/New_York',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false
    };
    
    const etTimeStr = now.toLocaleString('en-US', etOptions);
    const [etHours, etMinutes] = etTimeStr.split(':').map(Number);
    const etHoursDecimal = etHours + (etMinutes / 60);
    
    // Get Eastern day of week
    const etDayOptions: Intl.DateTimeFormatOptions = {
      timeZone: 'America/New_York',
      weekday: 'short'
    };
    const etDay = now.toLocaleString('en-US', etDayOptions);
    
    // Check if it's a weekday and within market hours (9:30 AM to 4:00 PM ET)
    const isWeekday = !['Sat', 'Sun'].includes(etDay);
    const isMarketHours = etHoursDecimal >= 9.5 && etHoursDecimal < 16;
    
    return isWeekday && isMarketHours;
  }

  private determineMarketStatus(): string {
    const now = new Date();
    
    // Get Eastern Time
    const etOptions: Intl.DateTimeFormatOptions = {
      timeZone: 'America/New_York',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false
    };
    
    const etTimeStr = now.toLocaleString('en-US', etOptions);
    const [etHours, etMinutes] = etTimeStr.split(':').map(Number);
    const etHoursDecimal = etHours + (etMinutes / 60);
    
    // Get Eastern day of week
    const etDayOptions: Intl.DateTimeFormatOptions = {
      timeZone: 'America/New_York',
      weekday: 'short'
    };
    const etDay = now.toLocaleString('en-US', etDayOptions);
    
    // Check market status
    if (['Sat', 'Sun'].includes(etDay)) {
      return 'closed-weekend';
    } else if (etHoursDecimal < 4) {
      return 'closed-overnight';
    } else if (etHoursDecimal < 9.5) {
      return 'pre-market';
    } else if (etHoursDecimal < 16) {
      return 'open';
    } else if (etHoursDecimal < 20) {
      return 'after-hours';
    } else {
      return 'closed';
    }
  }

  async getPriceChanges(symbol: string, timeRange: TimeRange = '1d'): Promise<{
    currentPrice: number;
    previousClose: number;
    dayChange: number;
    dayChangePercent: number;
    periodChange?: number;
    periodChangePercent?: number;
    high?: number;
    low?: number;
    open?: number;
    volume?: number;
    timestamp: number;
  }> {
    try {
      // First get the current price data (for consistent day change)
      const currentPriceData = await this.getCurrentPrice(symbol);
      
      // Then get historical data for the requested timeframe (for period change)
      const stockData = await this.getPublicStockData(symbol, timeRange);
      
      if (!stockData || stockData.length === 0) {
        // If we couldn't get historical data, return just day change
        return {
          currentPrice: currentPriceData.currentPrice,
          previousClose: currentPriceData.previousClose,
          dayChange: currentPriceData.change,
          dayChangePercent: currentPriceData.changePercent,
          timestamp: currentPriceData.timestamp
        };
      }
      
      // Calculate period change using first and last points of the period
      const firstPoint = stockData[0];
      const lastPoint = stockData[stockData.length - 1];
      
      // Use the firstPoint's open price for period change calculation
      const periodStartPrice = firstPoint.o;
      // Use the latest price from currentPriceData as the most up-to-date value
      const periodEndPrice = currentPriceData.currentPrice;
      
      const periodChange = Number((periodEndPrice - periodStartPrice).toFixed(2));
      const periodChangePercent = Number(((periodChange / periodStartPrice) * 100).toFixed(2));
      
      // For day stats (high, low, etc.), try to get from today's data if available
      let dayHigh = currentPriceData.high;
      let dayLow = currentPriceData.low;
      let dayOpen = currentPriceData.open;
      let dayVolume = currentPriceData.volume;
      
      // If we have today's data in stockData, use that for more accurate high/low
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayTimestamp = today.getTime();
      
      const todayData = stockData.filter(point => point.t >= todayTimestamp);
      if (todayData.length > 0) {
        dayHigh = Math.max(...todayData.map(point => point.h));
        dayLow = Math.min(...todayData.map(point => point.l));
        dayOpen = todayData[0].o;
        dayVolume = todayData.reduce((sum, point) => sum + point.v, 0);
      }
      
      return {
        currentPrice: currentPriceData.currentPrice,
        previousClose: currentPriceData.previousClose,
        dayChange: currentPriceData.change,
        dayChangePercent: currentPriceData.changePercent,
        periodChange,
        periodChangePercent,
        high: dayHigh,
        low: dayLow,
        open: dayOpen,
        volume: dayVolume,
        timestamp: currentPriceData.timestamp
      };
    } catch (error) {
      console.error(`Failed to get price changes for ${symbol}:`, error);
      throw error;
    }
  }

  // Improved version of getStockPrice that uses the central getCurrentPrice method
  async getStockPrice(symbol: string): Promise<{
    price: number;
    change: number;
    changePercent: number;
  }> {
    try {
      const priceData = await this.getCurrentPrice(symbol);
      return {
        price: priceData.currentPrice,
        change: priceData.change,
        changePercent: priceData.changePercent
      };
    } catch (error) {
      console.error(`Failed to get stock price for ${symbol}:`, error);
      return {
        price: 0,
        change: 0,
        changePercent: 0
      };
    }
  }

  clearCache(symbol?: string): void {
    if (symbol) {
      // Clear only caches for a specific symbol
      this.cache.forEach((value, key) => {
        if (key.includes(symbol)) {
          this.cache.delete(key);
        }
      });
      this.currentPricesCache.delete(`current-price:${symbol}`);
    } else {
      // Clear all caches
      this.cache.clear();
      this.currentPricesCache.clear();
    }
  }
}

export const marketDataService = MarketDataService.getInstance();
